import { useRef, useEffect, useState, useCallback } from "react";
import PlacedField from "./PlacedField";
import { FIELD_TYPES } from "./fieldTypes";
import { colors } from "../../styles/theme";

const RENDER_SCALE = 1.5; // render at higher resolution for crispness

export default function PdfPageViewer({
  page, pageNum, fields, signers, activeSigner,
  onAddField, onMoveField, onResizeField, onDeleteField,
  onPageDimensions, containerWidth,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const rendered = useRef(false);

  useEffect(() => {
    if (!page || !canvasRef.current) return;

    const viewport = page.getViewport({ scale: 1 });
    const pdfW = viewport.width;
    const pdfH = viewport.height;

    // Fit to container width
    const displayW = containerWidth - 40; // padding
    const displayScale = displayW / pdfW;
    const displayH = pdfH * displayScale;
    setDisplaySize({ w: displayW, h: displayH });

    // Report dimensions for coordinate conversion
    onPageDimensions(pageNum, { canvasW: displayW, canvasH: displayH, pdfW, pdfH });

    // Render at higher scale for crispness
    const renderViewport = page.getViewport({ scale: displayScale * RENDER_SCALE });
    const canvas = canvasRef.current;
    canvas.width = renderViewport.width;
    canvas.height = renderViewport.height;
    canvas.style.width = `${displayW}px`;
    canvas.style.height = `${displayH}px`;

    if (!rendered.current) {
      rendered.current = true;
      page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: renderViewport,
      });
    }
  }, [page, containerWidth]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData("application/field-type");
    if (!fieldType) return;

    const def = FIELD_TYPES.find((f) => f.type === fieldType);
    if (!def) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left - def.defaultW / 2;
    const y = e.clientY - rect.top - def.defaultH / 2;

    onAddField({
      id: crypto.randomUUID(),
      type: fieldType,
      page: pageNum,
      x: Math.max(0, Math.min(x, displaySize.w - def.defaultW)),
      y: Math.max(0, Math.min(y, displaySize.h - def.defaultH)),
      width: def.defaultW,
      height: def.defaultH,
      recipientIndex: activeSigner,
    });
  }, [pageNum, activeSigner, onAddField, displaySize]);

  const pageFields = fields.filter((f) => f.page === pageNum);

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        fontSize: 10, color: colors.textSecondary, textAlign: "center",
        marginBottom: 6, fontWeight: 600,
      }}>
        Page {pageNum}
      </div>
      <div
        ref={containerRef}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          position: "relative",
          width: displaySize.w,
          height: displaySize.h,
          margin: "0 auto",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
          borderRadius: 4,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        <canvas ref={canvasRef} />
        {pageFields.map((f) => (
          <PlacedField
            key={f.id}
            field={f}
            signers={signers}
            onMove={onMoveField}
            onResize={onResizeField}
            onDelete={onDeleteField}
            containerRef={containerRef}
          />
        ))}
      </div>
    </div>
  );
}
